import React from "react";
import * as Sentry from "@sentry/react";
import { captureException } from "@/lib/sentry";

type State = { hasError: boolean; error?: Error; eventId?: string };

export default class ErrorBoundary extends React.Component<React.PropsWithChildren, State> {
  constructor(props: React.PropsWithChildren) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // Report error to Sentry with component stack
    const eventId = Sentry.captureException(error, {
      contexts: {
        react: {
          componentStack: info.componentStack,
        },
      },
    });
    
    this.setState({ eventId });
    
    // Also log to console in development
    if (import.meta.env.DEV) {
      console.error("Unhandled error in component tree:", error, info);
    }
  }

  handleReportFeedback = () => {
    if (this.state.eventId) {
      Sentry.showReportDialog({ eventId: this.state.eventId });
    }
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center px-6">
          <div className="max-w-xl text-center">
            <h2 className="text-2xl font-semibold mb-4">Something went wrong</h2>
            <p className="text-muted-foreground mb-6">
              An unexpected error occurred. Refresh the page or contact support if the problem persists.
            </p>
            {this.state.error && import.meta.env.DEV && (
              <pre className="text-left text-xs bg-muted p-4 rounded mb-6 overflow-auto max-h-40">
                {this.state.error.stack}
              </pre>
            )}
            <div className="flex justify-center gap-3">
              <a href="/" className="text-primary underline">Return home</a>
              <button onClick={() => window.location.reload()} className="underline text-muted-foreground">
                Reload
              </button>
              {this.state.eventId && (
                <button onClick={this.handleReportFeedback} className="underline text-muted-foreground">
                  Report feedback
                </button>
              )}
            </div>
          </div>
        </div>
      );
    }

    return this.props.children as React.ReactElement;
  }
}
